import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

/**
 * ResponsiveDialog renders a centered Dialog on desktop (md+) and a
 * bottom-sheet Drawer (vaul) on mobile, with one unified API.
 *
 * Usage:
 *   <ResponsiveDialog open={open} onOpenChange={setOpen}>
 *     <ResponsiveDialogContent className="max-w-3xl">
 *       <ResponsiveDialogHeader>
 *         <ResponsiveDialogTitle>Title</ResponsiveDialogTitle>
 *       </ResponsiveDialogHeader>
 *       ...body...
 *       <ResponsiveDialogFooter>...</ResponsiveDialogFooter>
 *     </ResponsiveDialogContent>
 *   </ResponsiveDialog>
 */

type Ctx = { isMobile: boolean; mobileFullscreen?: boolean };
const ResponsiveCtx = React.createContext<Ctx>({ isMobile: false });

export function ResponsiveDialog({
  open,
  onOpenChange,
  mobileFullscreen,
  children,
}: {
  open?: boolean;
  onOpenChange?: (v: boolean) => void;
  mobileFullscreen?: boolean;
  children: React.ReactNode;
}) {
  const isMobile = useIsMobile();
  const Root = isMobile && !mobileFullscreen ? DrawerPrimitive.Root : DialogPrimitive.Root;
  return (
    <ResponsiveCtx.Provider value={{ isMobile, mobileFullscreen }}>
      <Root
        open={open}
        onOpenChange={onOpenChange}
        {...(isMobile && !mobileFullscreen
          ? ({ shouldScaleBackground: false, repositionInputs: false, dismissible: true } as any)
          : {})}
      >
        {children}
      </Root>
    </ResponsiveCtx.Provider>
  );
}

/**
 * Tracks the mobile visual viewport so the bottom sheet shrinks when the
 * on-screen keyboard opens (instead of being pushed off-screen).
 */
function useViewportHeight(enabled: boolean) {
  const [vp, setVp] = React.useState<{ height: number; bottom: number } | null>(null);
  React.useEffect(() => {
    if (!enabled || typeof window === "undefined") return;
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () =>
      setVp({
        height: vv.height,
        // Distância do fundo do layout viewport até o fundo visível:
        // quando o teclado abre, o sheet sobe junto em vez de ficar atrás.
        bottom: Math.max(0, window.innerHeight - vv.height - vv.offsetTop),
      });
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [enabled]);
  return vp;
}

export const ResponsiveDialogTrigger = ({ children, ...props }: any) => {
  const { isMobile, mobileFullscreen } = React.useContext(ResponsiveCtx);
  const Trigger = isMobile && !mobileFullscreen ? DrawerPrimitive.Trigger : DialogPrimitive.Trigger;
  return <Trigger {...props}>{children}</Trigger>;
};

export const ResponsiveDialogClose = ({ children, ...props }: any) => {
  const { isMobile, mobileFullscreen } = React.useContext(ResponsiveCtx);
  const Close = isMobile && !mobileFullscreen ? DrawerPrimitive.Close : DialogPrimitive.Close;
  return <Close {...props}>{children}</Close>;
};

export const ResponsiveDialogContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    /** Optional className applied only on desktop (Dialog). */
    desktopClassName?: string;
    /** Optional className applied only on mobile (Drawer). */
    mobileClassName?: string;
    /** Hide the X close button on desktop. */
    hideClose?: boolean;
    /** Render the mobile drawer as a full-screen sheet. */
    mobileFullscreen?: boolean;
  }
>(({ className, desktopClassName, mobileClassName, hideClose, mobileFullscreen, children, ...props }, ref) => {
  const { isMobile } = React.useContext(ResponsiveCtx);
  const vp = useViewportHeight(isMobile);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const setRefs = React.useCallback(
    (node: HTMLDivElement | null) => {
      contentRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [ref],
  );

  // Auto-scroll focused inputs/textareas into view above the keyboard.
  React.useEffect(() => {
    if (!isMobile) return;
    const root = contentRef.current;
    if (!root) return;
    const onFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (!target.matches("input, textarea, [contenteditable='true']")) return;
      // wait for keyboard animation
      setTimeout(() => {
        target.scrollIntoView({ block: "center", behavior: "smooth" });
      }, 250);
    };
    root.addEventListener("focusin", onFocusIn);
    return () => root.removeEventListener("focusin", onFocusIn);
  }, [isMobile]);

  if (isMobile && !mobileFullscreen) {
    return (
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md transition-colors data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DrawerPrimitive.Content
          ref={setRefs}
          style={
            vp
              ? mobileFullscreen
                ? { height: `${vp.height}px`, maxHeight: `${vp.height}px`, bottom: `${vp.bottom}px` }
                : { maxHeight: `${Math.max(vp.height - 16, 240)}px`, bottom: `${vp.bottom}px` }
              : undefined
          }
          className={cn(
            "fixed inset-x-0 bottom-0 z-50 flex max-h-[92dvh] flex-col rounded-t-3xl border border-border bg-background shadow-2xl outline-none",
            mobileFullscreen && "top-0 max-h-none rounded-none border-0",
            className,
            mobileClassName,
          )}
          {...props}
        >
          <div
            data-vaul-drag-handle
            className="flex w-full shrink-0 cursor-grab items-center justify-center py-2.5 active:cursor-grabbing"
          >
            <div className="h-1.5 w-12 rounded-full bg-neutral-400/60" />
          </div>
          {children}
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    );
  }

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border border-border bg-background shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-2xl",
          className,
          desktopClassName,
        )}
        {...props}
      >
        {children}
        {!hideClose && (
          <DialogPrimitive.Close className="absolute right-4 top-4 z-10 rounded-full p-1.5 text-muted-foreground opacity-70 ring-offset-background transition-opacity hover:bg-surface hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring">
            <X className="h-4 w-4" />
            <span className="sr-only">Fechar</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});
ResponsiveDialogContent.displayName = "ResponsiveDialogContent";

export const ResponsiveDialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col gap-1 text-left", className)} {...props} />
);

export const ResponsiveDialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)} {...props} />
);

export const ResponsiveDialogTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => {
  const { isMobile } = React.useContext(ResponsiveCtx);
  const Title = isMobile ? DrawerPrimitive.Title : DialogPrimitive.Title;
  return (
    <Title
      ref={ref}
      className={cn("font-display text-lg font-bold leading-tight tracking-tight", className)}
      {...props}
    />
  );
});
ResponsiveDialogTitle.displayName = "ResponsiveDialogTitle";

export const ResponsiveDialogDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { isMobile } = React.useContext(ResponsiveCtx);
  const Description = isMobile ? DrawerPrimitive.Description : DialogPrimitive.Description;
  return (
    <Description
      ref={ref}
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
});
ResponsiveDialogDescription.displayName = "ResponsiveDialogDescription";

/** Hook for callers that need to branch behavior. */
export function useResponsiveDialog() {
  return React.useContext(ResponsiveCtx);
}
